import { ColumnFilter } from '@tanstack/react-table';
import { useMemo } from 'react';

import { createFilterSectionHelper, FilterItemValue, useFilter } from '@/components-new/filter/use-filter';
import { SHARED_FILTER_SECTIONS, getMultiFilterLabel, StateFilter, PoolsFilter, PDLStatusFilter, PATypeFilter } from '@/components-new/filters/shared-filters';
import { CodeBasedLookup } from '@/types/lookup';

export type DrugInfoFilterSchema = {
  state: CodeBasedLookup[];
  pdlStatus: CodeBasedLookup[];
  paType: CodeBasedLookup[];
  pool: CodeBasedLookup[];
}

const filterSectionHelper = createFilterSectionHelper<DrugInfoFilterSchema>();

const filterSections = [
  filterSectionHelper.defineSection(
    'state',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.state;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('States', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <StateFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'pool',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.pool;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('Pool', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PoolsFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'pdlStatus',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.pdlStatus;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('PDL Statuses', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PDLStatusFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'paType',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.paType;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('PA Types', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PATypeFilter {...ctx}/>
    }
  )
];

const labelBasedSerializer = (filter: FilterItemValue<DrugInfoFilterSchema>): ColumnFilter => ({
  id: filter.key,
  value: filter.value.map(value => value.label)
});

const serializerMap = {
  state: labelBasedSerializer,
  pdlStatus: labelBasedSerializer,
  paType: labelBasedSerializer,
  pool: labelBasedSerializer
} as const;

const serializeTableFilters = (filters: FilterItemValue<DrugInfoFilterSchema>[]): ColumnFilter[] => {
  return filters.map(filter => serializerMap[filter.key](filter));
};

export const useDrugInfoFilters = () => {
  const filter = useFilter({ filterSections });

  const tableColumnFilters = useMemo(() => serializeTableFilters(filter.values), [filter.values]);

  return {
    filter,
    tableColumnFilters
  };
};
